import React, { useEffect, useState } from 'react';
import { Card, CardContent, Box, Typography, Button, Menu, MenuItem, Avatar, AvatarGroup, Divider, Tooltip, IconButton } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StudentGetUser from 'utilities/api/StudentAllUserAPIs';
import ImageBaseURL from 'utilities/api/axios';

const statusColors = {
  "1": { background: '#FFE5E5', color: '#C90505' }, // Not Started (Red)
  "2": { background: '#FEFFD2', color: '#D0A300' }, // In Progress (Yellow)
  "3": { background: '#E5FFF1', color: '#057D35' }, // Completed (Green)
};

const ProjectCard = ({ project, onEdit, onView, user, showVisibility, allUsers }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [teammatesData, setTeammatesData] = useState([]);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const status = project.progress_status;
  const statusColor = statusColors[status];

  const getProfilePicUrl = (profilePicPath) => {
    return profilePicPath ? `${ImageBaseURL.defaults.baseURL}${profilePicPath}` : '';
  };

  // Fetch user data for each teammate by username
  useEffect(() => {
    const fetchTeammatesData = async () => {
      const fetchedData = await Promise.all(
        project.teammates_names.map(async (username) => {
          try {
            const userData = await StudentGetUser.UserDetailsGet(username);
            return userData[0]; // Assuming the API returns an array and we take the first item
          } catch (error) {
            console.error(`Error fetching data for ${username}:`, error);
            return null;
          }
        })
      );
      setTeammatesData(fetchedData.filter(data => data !== null)); // Filter out any failed fetches
    };

    fetchTeammatesData();
  }, [project.teammates_names]);

  // Check if the current user is one of the teammates
  const isTeammate = project.teammates.includes(user.id);

  return (
    <Card sx={{
      width: '100%',
      height: '100%',
      borderRadius: '16px',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
      border: '1px solid #E0E4EC',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
      }
    }}>
      <CardContent sx={{ flex: '1 0 auto', padding: '12px', paddingBottom: '0 !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: statusColor.background, borderRadius: '50px', padding: '4px 12px' }}>
              <Box sx={{ height: 6, width: 6, borderRadius: '50%', backgroundColor: statusColor.color, marginRight: 1 }} />
              <Typography variant="caption" sx={{ color: statusColor.color, fontWeight: 600 }}>
                {status == '1' ? 'Not Started' : status == '2' ? 'In Progress' : 'Completed'}
              </Typography>
            </Box>
            {project.is_patent_filed && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                backgroundColor: '#FFF4E5', 
                borderRadius: '50px', 
                padding: '3px 10px',
                border: '1px solid #FFB74D'
              }}>
                <Typography variant="caption" sx={{ color: '#E65100', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                  Patent Filed
                </Typography>
              </Box>
            )}
          </Box>
          <Box>
            {(showVisibility) ? (
              <IconButton
                size="small"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={() => onView(project)}
                sx={{ backgroundColor: '#F3F4F6' }}
              >
                <VisibilityIcon fontSize="small" sx={{ color: '#6B7280' }} />
              </IconButton>
            ) : (
              (user.id === project.student || isTeammate) && (
                <IconButton
                  size="small"
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleMenuOpen}
                  sx={{ backgroundColor: '#F3F4F6' }}
                >
                  <MoreHorizIcon fontSize="small" sx={{ color: '#6B7280' }} />
                </IconButton>
              )
            )}
            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose} MenuListProps={{ 'aria-labelledby': 'basic-button' }}>
              <MenuItem onClick={() => { handleMenuClose(); onEdit(project); }}>Edit Project</MenuItem>
            </Menu>
          </Box>
        </Box>

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1.2rem',
            color: '#1F2A44',
            marginBottom: '8px',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {project.title}
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4B5563', marginBottom: '4px' }}>Description</Typography>
        <Typography variant="body2" sx={{
          color: '#6B7280',
          marginBottom: '12px',
          display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, textOverflow: 'ellipsis',
          lineHeight: 1.5
        }}>
          {project.description || "No description provided."}
        </Typography>

        <Box sx={{ display: 'inline-block', backgroundColor: '#EEF2FF', padding: '4px 10px', borderRadius: '6px' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#4F46E5' }}>
            {project.type === '1' ? 'Academic' : project.type === '2' ? 'Research' : project.type === '3' ? 'Personal' : 'Other'}
          </Typography>
        </Box>
      </CardContent>

      <Box sx={{ padding: '16px 12px 8px 12px' }}>
        <Divider sx={{ marginBottom: '12px', borderColor: '#E5E7EB' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#9CA3AF', fontWeight: 500 }}>
            {project.date}
          </Typography>
          <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12, border: '2px solid #FFF' } }}>
            {teammatesData.map((member, index) => (
              <Tooltip key={index} title={member?.username || ''}>
                <Avatar src={getProfilePicUrl(member?.profile_pic)} alt={member?.name} />
              </Tooltip>
            ))}
            <Tooltip title={user.username}>
              <Avatar src={getProfilePicUrl(user.profile_pic)} alt={user.username} sx={{ border: '2px solid #E79A2B !important' }} />
            </Tooltip>
          </AvatarGroup>
        </Box>
      </Box>
    </Card>
  );
};

export default ProjectCard;
